/**
 * Route optimizer — finds the most efficient order to visit zones
 * in a grocery store using nearest-neighbor + 2-opt improvement.
 */

import { findPath } from './pathfinding.js'
import { getCentroid, findNearestWalkable, CellType, findCellsOfType } from './gridHelpers.js'

/**
 * Calculates the optimal route through a store.
 *
 * @param {string[][]} grid — 2D grid of CellType values
 * @param {Array<{ category: string, cells: Array<[number, number]> }>} zones — zone definitions
 * @param {Array<{ name: string, category: string }>} items — shopping list items
 * @returns {{
 *   stops: Array<{ category: string, items: string[], x: number, y: number, walkableX: number, walkableY: number }>,
 *   paths: Array<Array<{ x: number, y: number }>>,
 *   totalDistance: number
 * }}
 */
export function calculateRoute(grid, zones, items) {
  // 1. Find entrance and checkout
  const entranceCells = findCellsOfType(grid, CellType.ENTRANCE)
  const checkoutCells = findCellsOfType(grid, CellType.CHECKOUT)

  const entrance = entranceCells.length > 0
    ? { x: entranceCells[0][0], y: entranceCells[0][1] }
    : { x: 0, y: grid.length - 1 }

  const checkout = checkoutCells.length > 0
    ? { x: checkoutCells[0][0], y: checkoutCells[0][1] }
    : { x: grid[0].length - 1, y: grid.length - 1 }

  // 2. Group items by category and map to zone centroids
  const itemsByCategory = new Map()
  for (const item of items) {
    if (item.checked) continue // Skip already collected items
    const cat = item.category || 'overig'
    if (!itemsByCategory.has(cat)) itemsByCategory.set(cat, [])
    itemsByCategory.get(cat).push(item.name)
  }

  // Build stops: one per unique category that has items
  const stops = []
  for (const [category, itemNames] of itemsByCategory) {
    const zone = zones.find(z => z.category === category)
    const cells = zone ? JSON.parse(typeof zone.cells === 'string' ? zone.cells : JSON.stringify(zone.cells)) : []

    let centroid
    if (cells.length > 0) {
      centroid = getCentroid(cells)
    } else {
      // No zone defined — place at grid center as fallback
      centroid = { x: Math.floor(grid[0].length / 2), y: Math.floor(grid.length / 2) }
    }

    const walkable = findNearestWalkable(grid, centroid.x, centroid.y)

    stops.push({
      category,
      items: itemNames,
      x: centroid.x,
      y: centroid.y,
      walkableX: walkable.x,
      walkableY: walkable.y,
    })
  }

  if (stops.length === 0) {
    return { stops: [], paths: [], totalDistance: 0 }
  }

  // 3. Nearest-neighbor ordering
  const ordered = nearestNeighborOrder(grid, stops, entrance, checkout)

  // 4. 2-opt improvement
  const improved = twoOptImprove(grid, ordered, entrance, checkout)

  // 5. Calculate paths between consecutive stops
  const allPoints = [
    { x: entrance.x, y: entrance.y },
    ...improved.map(s => ({ x: s.walkableX, y: s.walkableY })),
    { x: checkout.x, y: checkout.y },
  ]

  const paths = []
  let totalDistance = 0

  for (let i = 0; i < allPoints.length - 1; i++) {
    const path = findPath(grid, allPoints[i].x, allPoints[i].y, allPoints[i + 1].x, allPoints[i + 1].y)
    paths.push(path)
    totalDistance += Math.max(0, path.length - 1)
  }

  return { stops: improved, paths, totalDistance }
}

/**
 * Orders stops using nearest-neighbor heuristic.
 * Start at entrance, greedily visit nearest unvisited stop, end at checkout.
 */
function nearestNeighborOrder(grid, stops, entrance, _checkout) {
  const remaining = [...stops]
  const ordered = []
  let currentX = entrance.x
  let currentY = entrance.y

  while (remaining.length > 0) {
    let nearestIdx = 0
    let nearestDist = Infinity

    for (let i = 0; i < remaining.length; i++) {
      const dist = Math.abs(currentX - remaining[i].walkableX) + Math.abs(currentY - remaining[i].walkableY)
      if (dist < nearestDist) {
        nearestDist = dist
        nearestIdx = i
      }
    }

    const next = remaining.splice(nearestIdx, 1)[0]
    ordered.push(next)
    currentX = next.walkableX
    currentY = next.walkableY
  }

  return ordered
}

/**
 * 2-opt improvement: swap pairs of edges to reduce total distance.
 * Uses Manhattan distance for speed (avoids repeated A* calls).
 */
function twoOptImprove(grid, stops, entrance, checkout) {
  if (stops.length <= 2) return stops

  const result = [...stops]
  let improved = true
  let iterations = 0
  const maxIterations = 100

  const dist = (a, b) => Math.abs(a.walkableX - b.walkableX) + Math.abs(a.walkableY - b.walkableY)
  const entrancePoint = { walkableX: entrance.x, walkableY: entrance.y }
  const checkoutPoint = { walkableX: checkout.x, walkableY: checkout.y }

  while (improved && iterations < maxIterations) {
    improved = false
    iterations++

    for (let i = 0; i < result.length - 1; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const prevI = i === 0 ? entrancePoint : result[i - 1]
        const nextJ = j === result.length - 1 ? checkoutPoint : result[j + 1]

        const currentDist = dist(prevI, result[i]) + dist(result[j], nextJ)
        const newDist = dist(prevI, result[j]) + dist(result[i], nextJ)

        if (newDist < currentDist) {
          // Reverse the segment between i and j
          const segment = result.slice(i, j + 1).reverse()
          result.splice(i, j - i + 1, ...segment)
          improved = true
        }
      }
    }
  }

  return result
}
